import {
  Alert,
  Badge,
  Button,
  Card,
  IconButton,
  Spinner,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { FC, useMemo, useState } from 'react';
import { LuPencil, LuPlus, LuTrash } from 'react-icons/lu';
import ManageUserModal, { ManageUserFormValues } from './ManageUserModal';
import DeleteUserDialog from './components/DeleteUserDialog';
import { useCurrentOrganization } from '../organizations/hooks';
import {
  canManageOrganizationMembers,
  formatOrganizationRoleLabel,
} from '../organizations/roles';
import {
  CreateUserInput,
  OrganizationUser,
  UpdateUserInput,
  useUsersSettings,
} from './hooks';

const UsersSettingsTab: FC = () => {
  const currentOrganization = useCurrentOrganization();
  const {
    users,
    isLoading,
    loadError,
    refresh,
    createUser,
    updateUser,
    deleteUser,
    isMutating,
    mutateError,
  } = useUsersSettings();

  const [modalState, setModalState] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; member: OrganizationUser }
    | null
  >(null);
  const [pendingDeletion, setPendingDeletion] = useState<OrganizationUser | null>(
    null
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const canManageMembers = canManageOrganizationMembers(
    currentOrganization?.role
  );

  const closeModal = () => setModalState(null);

  const handleSubmit = async (values: ManageUserFormValues) => {
    if (!modalState) return;

    if (modalState.mode === 'create') {
      const payload: CreateUserInput = {
        email: values.email.trim(),
        role: values.role,
      };
      await createUser(payload);
    } else {
      const payload: UpdateUserInput = {
        role: values.role,
      };
      await updateUser(modalState.member.id, payload);
    }
  };

  const handleDelete = async () => {
    if (!pendingDeletion) return;
    setDeleteError(null);
    try {
      await deleteUser(pendingDeletion.id);
      toaster.create({
        type: 'success',
        title: 'User removed',
        description: 'The user has been removed from the organization.',
      });
      setPendingDeletion(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to remove user.';
      setDeleteError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to remove user',
        description: message,
      });
    }
  };

  const tableContent = useMemo(() => {
    if (isLoading) {
      return (
        <Stack align="center" gap="3" py="6">
          <Spinner />
          <Text fontSize="sm" color="fg.muted">
            Loading users…
          </Text>
        </Stack>
      );
    }

    if (loadError) {
      return (
        <Stack gap="4">
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Unable to load users</Alert.Title>
              <Alert.Description>{loadError}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
          <Button onClick={() => void refresh()} alignSelf="flex-start">
            Try again
          </Button>
        </Stack>
      );
    }

    if (!currentOrganization) {
      return (
        <Alert.Root status="warning">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>No organization selected</Alert.Title>
            <Alert.Description>
              Choose an organization to manage its users.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      );
    }

    if (users.length === 0) {
      return (
        <Alert.Root status="info">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>No users yet</Alert.Title>
            <Alert.Description>
              Add your teammates to collaborate together.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      );
    }

    return (
      <Table.ScrollArea>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Email</Table.ColumnHeader>
              <Table.ColumnHeader>Role</Table.ColumnHeader>
              <Table.ColumnHeader>Joined</Table.ColumnHeader>
              {canManageMembers ? (
                <Table.ColumnHeader textAlign="right" width="1%">
                  Actions
                </Table.ColumnHeader>
              ) : null}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {users.map((member) => (
              <Table.Row key={member.id}>
                <Table.Cell>
                  <Text fontWeight="medium">
                    {member.user.name ?? '—'}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" color="fg.muted">
                    {member.user.email ?? '—'}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge variant="subtle">
                    {formatOrganizationRoleLabel(member.role)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" color="fg.muted">
                    {new Date(member.joinedAt).toLocaleDateString()}
                  </Text>
                </Table.Cell>
                {canManageMembers ? (
                  <Table.Cell textAlign="right">
                    <Stack direction="row" justify="flex-end" gap="1">
                      <IconButton
                        aria-label={`Edit ${member.user.email ?? 'user'}`}
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setModalState({ mode: 'edit', member })
                        }
                      >
                        <LuPencil />
                      </IconButton>
                      <IconButton
                        aria-label={`Remove ${member.user.email ?? 'user'}`}
                        size="sm"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => {
                          setDeleteError(null);
                          setPendingDeletion(member);
                        }}
                      >
                        <LuTrash />
                      </IconButton>
                    </Stack>
                  </Table.Cell>
                ) : null}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
    );
  }, [currentOrganization, isLoading, loadError, refresh, users]);

  return (
    <Stack gap="6">
      <Card.Root>
        <Card.Header
          gap="3"
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack gap="1">
            <Card.Title>Users</Card.Title>
            <Card.Description>
              Manage members in your organization.
            </Card.Description>
          </Stack>
          <Button
            size="sm"
            onClick={() => setModalState({ mode: 'create' })}
            disabled={!currentOrganization || !canManageMembers}
            display="inline-flex"
            alignItems="center"
            gap="2"
          >
            <LuPlus /> Add user
          </Button>
        </Card.Header>
        <Card.Body gap="4">
          {tableContent}
          {mutateError && !modalState && !pendingDeletion ? (
            <Text color="fg.error" fontSize="sm">
              {mutateError}
            </Text>
          ) : null}
          {currentOrganization && !canManageMembers ? (
            <Text color="fg.muted" fontSize="sm">
              You need Maintainer access to invite, edit, or remove members.
            </Text>
          ) : null}
        </Card.Body>
      </Card.Root>
      <ManageUserModal
        isOpen={modalState !== null}
        mode={modalState?.mode ?? 'create'}
        defaultValues={
          modalState && modalState.mode === 'edit'
            ? {
                email: modalState.member.user.email ?? '',
                role: modalState.member.role,
              }
            : undefined
        }
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
      <DeleteUserDialog
        isOpen={Boolean(pendingDeletion)}
        isLoading={isMutating}
        memberName={pendingDeletion?.user.name}
        memberEmail={pendingDeletion?.user.email}
        error={deleteError}
        onCancel={() => {
          setDeleteError(null);
          setPendingDeletion(null);
        }}
        onConfirm={handleDelete}
      />
    </Stack>
  );
};

export default UsersSettingsTab;
